import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, Enums } from '../db/supabase.types.ts'
import type {
  PendingTrait,
  TraitDisposition,
  TraitInsertPayload,
  TranslatedTrait,
} from './types.ts'
import { parseJSONData } from '../lib/json.ts'
import { translatableFieldSchema, type TranslatableField } from '../lib/languages.ts'
import { traitHasTranslations } from './utils.ts'

export async function insertTraits(
  {
    traits,
    defaultDisposition = 'positive',
    realm = 'real_estate',
  }: {
    traits: TraitInsertPayload[]
    defaultDisposition?: TraitDisposition
    realm?: Enums<'trait_realm'>
  },
  ctx: {
    translateTraits: (traits: string[]) => Promise<TranslatedTrait[]>
    getTraitEmbeddings: (traits: string[]) => Promise<number[][]>
    supabase: SupabaseClient<Database>
  }
) {
  const { translateTraits, getTraitEmbeddings, supabase } = ctx

  const translatedTraits = await Promise.all(
    traits.map(async (t) => {
      let translations: TranslatableField = t.translations || []
      let text: string = t.text

      if (!traitHasTranslations(t)) {
        const traitTranslations = await translateTraits([t.text])
        text = traitTranslations[0].text
        translations = traitTranslations[0].translations || []
      } else {
        text = t.text
        translations = t.translations || []
      }

      return {
        text: text.toLowerCase(),
        translations: translations.map((t) => ({
          ...t,
          text: t.text.toLowerCase(),
        })),
        disposition: t.disposition || defaultDisposition,
      }
    })
  )

  const embeddings = await getTraitEmbeddings(translatedTraits.map((t) => t.text))

  const insertData = translatedTraits.map((t, index) => ({
    text: t.text,
    embedding: `[${embeddings[index].join(',')}]`,
    translations: t.translations || [],
    realm,
  }))

  const { data: insertedTraits, error } = await supabase
    .from('traits')
    .upsert(insertData, {
      onConflict: 'text, realm',
      ignoreDuplicates: false,
    })
    .select('id, text, translations')

  if (error) {
    console.error(error)
    return []
  }

  return insertedTraits.map((trait) => {
    const translatedTrait = translatedTraits.find((tt) => tt.text === trait.text)

    return {
      id: trait.id,
      text: trait.text,
      disposition: translatedTrait?.disposition || defaultDisposition,
      translations:
        translatedTrait?.translations ||
        parseJSONData(trait.translations, translatableFieldSchema) ||
        [],
      assignment_type: 'assigned' as const,
    } satisfies PendingTrait
  })
}

export async function assignAssetTraits(
  {
    assetId,
    traits,
  }: {
    assetId: string
    traits: PendingTrait[]
  },
  ctx: { supabase: SupabaseClient<Database> }
) {
  const { supabase } = ctx

  const traitsToAssign = traits
    .filter((t) => !!t.id)
    .map((trait) => ({
      trait_id: trait.id!,
      object_type: 'asset' as const,
      object_id: assetId,
      disposition: trait.disposition,
      assignment_type: trait.assignment_type,
    }))

  const insertSuggestionAssignments = traitsToAssign.filter(
    (t) => t.assignment_type === 'suggested'
  )

  const insertAssignAssignments = traitsToAssign.filter((t) => t.assignment_type === 'assigned')

  const [{ error: assignmentError }, { error: suggestionError }] = await Promise.all([
    insertAssignAssignments.length !== 0
      ? supabase.from('trait_assignments').upsert(insertAssignAssignments, {
          onConflict: 'trait_id, object_type, object_id',
          ignoreDuplicates: false,
        })
      : Promise.resolve({ error: null }),
    insertSuggestionAssignments.length !== 0
      ? supabase.from('trait_assignments').upsert(insertSuggestionAssignments, {
          onConflict: 'trait_id, object_type, object_id',
          // Do not overwrite assignments that are potentially strongly assigned.
          ignoreDuplicates: true,
        })
      : Promise.resolve({ error: null }),
  ])

  if (assignmentError || suggestionError) {
    console.log('Error adding traits:', assignmentError || suggestionError)
  }

  return true
}
